import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Sparkles, Pencil, Trash2, Calendar as CalendarIcon } from "lucide-react";

const TYPE_LABEL = {
  vacances: { fr: "Vacances scolaires", en: "School break", color: "#d96552" },
  ferie: { fr: "Jour férié", en: "Public holiday", color: "#7c5dfa" },
  evenement: { fr: "Événement", en: "Event", color: "#1a2238" },
  sortie: { fr: "Sortie scolaire", en: "Field trip", color: "#2d9d78" },
  reunion: { fr: "Réunion", en: "Meeting", color: "#0ea5e9" },
  examen: { fr: "Examen", en: "Exam", color: "#dc2626" },
  rentree: { fr: "Rentrée", en: "Back to school", color: "#16a34a" },
  personnalise: { fr: "Personnalisé", en: "Custom", color: "#64748b" },
};

const SCHOOL_YEARS = ["2025-2026", "2026-2027"];
const ZONES = [
  { code: "A", label: "Zone A — Lyon, Bordeaux, Grenoble…" },
  { code: "B", label: "Zone B — Marseille, Lille, Strasbourg…" },
  { code: "C", label: "Zone C — Paris, Créteil, Versailles" },
];

const emptyForm = {
  type: "evenement",
  title_fr: "",
  title_en: "",
  start_date: "",
  end_date: "",
  all_day: true,
  color: "",
  description_fr: "",
  description_en: "",
  school_year: "2025-2026",
};

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US",
      { weekday: "short", day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

const monthKey = (iso) => {
  if (!iso) return "—";
  return iso.slice(0, 7); // YYYY-MM
};
const monthLabel = (key, lang) => {
  if (!key || key === "—") return "—";
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { month: "long", year: "numeric" });
};

export default function AdminCalendar() {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("2025-2026");
  const [zone, setZone] = useState("C");
  const [seeding, setSeeding] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    api.get(`/calendar/events?school_year=${encodeURIComponent(year)}`)
      .then(r => setItems(r.data))
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const seedFR = async () => {
    if (!window.confirm(lang === "fr"
      ? `Préremplir les vacances scolaires ${year} pour la zone ${zone} ? Les vacances déjà importées pour cette zone seront remplacées.`
      : `Pre-fill ${year} school vacations for zone ${zone}? Existing imported vacations for this zone will be replaced.`)) return;
    setSeeding(true);
    try {
      const r = await api.post(`/admin/calendar/seed-fr/${year}?zone=${zone}`);
      toast.success(lang === "fr"
        ? `${r.data.inserted} période(s) de vacances ajoutée(s)`
        : `${r.data.inserted} vacation period(s) added`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSeeding(false);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ ...emptyForm, school_year: year });
    setOpen(true);
  };

  const openEdit = (ev) => {
    setEditId(ev.id);
    setForm({
      type: ev.type || "evenement",
      title_fr: ev.title_fr || "",
      title_en: ev.title_en || "",
      start_date: ev.start_date || "",
      end_date: ev.end_date || "",
      all_day: ev.all_day !== false,
      color: ev.color || "",
      description_fr: ev.description_fr || "",
      description_en: ev.description_en || "",
      school_year: ev.school_year || year,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title_fr.trim() || !form.title_en.trim() || !form.start_date) {
      toast.error(lang === "fr" ? "Titre FR/EN et date de début requis" : "FR/EN title and start date required");
      return;
    }
    const payload = { ...form };
    if (!payload.end_date) delete payload.end_date;
    if (!payload.color) delete payload.color;
    if (!payload.description_fr) delete payload.description_fr;
    if (!payload.description_en) delete payload.description_en;
    try {
      if (editId) await api.patch(`/calendar/events/${editId}`, payload);
      else await api.post(`/calendar/events`, payload);
      toast.success(lang === "fr" ? "Enregistré" : "Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(lang === "fr" ? `Supprimer « ${ev.title_fr} » ?` : `Delete « ${ev.title_en} » ?`)) return;
    try {
      await api.delete(`/calendar/events/${ev.id}`);
      toast.success(lang === "fr" ? "Supprimé" : "Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const grouped = useMemo(() => {
    const map = {};
    for (const ev of items) {
      const k = monthKey(ev.start_date);
      if (!map[k]) map[k] = [];
      map[k].push(ev);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div data-testid="admin-calendar">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="overline mb-1">OFA France · Admin</p>
          <h1 className="font-serif text-3xl tracking-tight">
            {lang === "fr" ? "Calendrier scolaire" : "School calendar"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {lang === "fr"
              ? "Gérez les vacances scolaires, jours fériés, événements, sorties et réunions. Préremplissage officiel disponible pour les zones A, B et C."
              : "Manage school vacations, public holidays, events, field trips and meetings. Official pre-fill available for zones A, B and C."}
          </p>
        </div>
        <Button onClick={openCreate} data-testid="calendar-create-btn">
          <Plus className="w-4 h-4 mr-1.5" />
          {lang === "fr" ? "Nouvel événement" : "New event"}
        </Button>
      </div>

      {/* Toolbar */}
      <Card className="mb-5">
        <CardContent className="p-4 grid md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">{lang === "fr" ? "Année scolaire" : "School year"}</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="calendar-year"><SelectValue /></SelectTrigger>
              <SelectContent>{SCHOOL_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{lang === "fr" ? "Zone de vacances" : "Vacation zone"}</Label>
            <Select value={zone} onValueChange={setZone}>
              <SelectTrigger data-testid="calendar-zone"><SelectValue /></SelectTrigger>
              <SelectContent>{ZONES.map(z => <SelectItem key={z.code} value={z.code}>{z.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={seedFR} disabled={seeding} data-testid="calendar-seed-fr">
            <Sparkles className="w-4 h-4 mr-1.5" />
            {seeding
              ? (lang === "fr" ? "Importation…" : "Importing…")
              : (lang === "fr" ? `Préremplir vacances ${year} · Zone ${zone}` : `Pre-fill ${year} vacations · Zone ${zone}`)}
          </Button>
          <div className="text-xs text-muted-foreground self-center">
            {items.length} {lang === "fr" ? "événement(s)" : "event(s)"}
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-center py-8 text-muted-foreground text-sm">{lang === "fr" ? "Chargement…" : "Loading…"}</div>}

      {!loading && items.length === 0 && (
        <Card><CardContent className="p-10 text-center text-muted-foreground text-sm">
          <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
          {lang === "fr"
            ? "Aucun événement. Cliquez sur « Préremplir vacances » pour démarrer ou ajoutez un événement personnalisé."
            : "No events. Click « Pre-fill vacations » to start or add a custom event."}
        </CardContent></Card>
      )}

      {!loading && grouped.map(([mk, evs]) => (
        <div key={mk} className="mb-4">
          <h3 className="font-display font-semibold text-xs uppercase tracking-wider text-muted-foreground mb-2">
            {monthLabel(mk, lang)}
          </h3>
          <Card>
            <CardContent className="p-0">
              {evs.map(ev => {
                const meta = TYPE_LABEL[ev.type] || TYPE_LABEL.personnalise;
                const color = ev.color || meta.color;
                return (
                  <div key={ev.id} className="flex items-center gap-3 p-4 border-b border-border/60 last:border-b-0 hover:bg-secondary/20"
                       data-testid={`calendar-row-${ev.id}`}>
                    <div className="w-1 self-stretch rounded-full" style={{ background: color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-semibold text-sm">{lang === "fr" ? ev.title_fr : ev.title_en}</span>
                        <Badge variant="outline" className="text-[10px]" style={{ borderColor: `${color}55`, color }}>
                          {meta[lang]}
                        </Badge>
                        {ev.source && <Badge variant="secondary" className="text-[10px]">{ev.source}</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                        {fmtDate(ev.start_date, lang)}
                        {ev.end_date && ev.end_date !== ev.start_date && ` → ${fmtDate(ev.end_date, lang)}`}
                      </div>
                      {(ev.description_fr || ev.description_en) && (
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {lang === "fr" ? ev.description_fr : ev.description_en}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(ev)} data-testid={`calendar-edit-${ev.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(ev)} data-testid={`calendar-delete-${ev.id}`}
                              className="text-destructive hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId
              ? (lang === "fr" ? "Modifier l'événement" : "Edit event")
              : (lang === "fr" ? "Nouvel événement" : "New event")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{lang === "fr" ? "Type" : "Type"}</Label>
              <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABEL).map(([k, v]) =>
                    <SelectItem key={k} value={k}>{v[lang]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{lang === "fr" ? "Titre (FR)" : "Title (FR)"}</Label>
                <Input data-testid="event-title-fr" value={form.title_fr}
                       onChange={e => setForm(f => ({ ...f, title_fr: e.target.value }))} />
              </div>
              <div>
                <Label>{lang === "fr" ? "Titre (EN)" : "Title (EN)"}</Label>
                <Input data-testid="event-title-en" value={form.title_en}
                       onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{lang === "fr" ? "Date de début" : "Start date"}</Label>
                <Input data-testid="event-start" type="date" value={form.start_date}
                       onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <Label>{lang === "fr" ? "Date de fin (optionnel)" : "End date (optional)"}</Label>
                <Input data-testid="event-end" type="date" value={form.end_date}
                       onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>{lang === "fr" ? "Année scolaire" : "School year"}</Label>
                <Select value={form.school_year} onValueChange={(v) => setForm(f => ({ ...f, school_year: v }))}>
                  <SelectTrigger data-testid="event-year"><SelectValue /></SelectTrigger>
                  <SelectContent>{SCHOOL_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{lang === "fr" ? "Couleur (optionnel)" : "Color (optional)"}</Label>
                <Input data-testid="event-color" type="color" value={form.color || "#1a2238"}
                       onChange={e => setForm(f => ({ ...f, color: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>{lang === "fr" ? "Description FR" : "Description FR"}</Label>
              <Textarea data-testid="event-desc-fr" rows={2} value={form.description_fr}
                        onChange={e => setForm(f => ({ ...f, description_fr: e.target.value }))} />
            </div>
            <div>
              <Label>{lang === "fr" ? "Description EN" : "Description EN"}</Label>
              <Textarea data-testid="event-desc-en" rows={2} value={form.description_en}
                        onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))} />
            </div>
            <Button onClick={save} className="w-full" data-testid="event-save">
              {lang === "fr" ? "Enregistrer" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
